import { Organization, OrganizationMember } from '@prisma/client'
import { useNavigate } from '@remix-run/react'
import { useEffect } from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { useSimpleLocalStorage } from '~/features/shared/hooks'
import { useEnsureConversation } from '../../../hooks/useEnsureConversation'
import { FindManyConversations } from '../../../hooks/useSdk'

type Props = {
  conversationId: string
  organization: Organization
  organizationMember: OrganizationMember
  organizationMembers: OrganizationMemberFromContext[]
  conversations: FindManyConversations
}

export const useLogic = ({
  organization,
  organizationMember,
  organizationMembers,
  conversationId,
  conversations,
}: Props) => {
  const navigate = useNavigate()

  const ensureConversation = useEnsureConversation({
    organization,
    organizationMember,
  })

  const selectItem = async (key: string) => {
    let conversation = conversations.find(candidate => candidate.id === key)

    const member = organizationMembers.find(member => member.id === key)

    if (!conversation && member) {
      conversation = (await ensureConversation.ensure(member)) as any
    }

    if (conversation) {
      navigate(`/organizations/${organization.id}/chat/${conversation.id}`)
    }
  }

  useEffect(() => {
    if (conversations.length === 0) {
      return
    }

    const conversationFound = conversations.find(
      candidate => candidate.id === conversationId,
    )
    const conversationDefault = conversations.find(candidate =>
      candidate.participants.every(item => !item.organizationContactId),
    )

    const canRedirect =
      conversationDefault &&
      location.pathname.endsWith('/chat') &&
      !conversationFound

    if (canRedirect) {
      navigate(
        `/organizations/${organization.id}/chat/${conversationDefault.id}`,
      )
    }
  }, [location.pathname, conversations, conversationId])

  useSimpleLocalStorage({
    isReady: conversations.length > 0,
    value: conversationId,
    valueDefault: conversations[0]?.id,
    keyLocalStorage: `${organization.id}-conversation`,
    onChange: conversationId => {
      selectItem(conversationId)
    },
  })

  return {
    selectItem,
  }
}
